import { useSelector, shallowEqual } from "react-redux"

// Per-user hooks that return primitives — only re-render when THIS user's value changes

export const useIsSoundMode = (userId) =>
    useSelector((state) => {
        const liveSession = state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        )
        if (liveSession?.live_room?.type === "individual") return true
        return state.liveMode?.soundModeIds?.includes(userId) ?? false
    })

export const useIsHandRaised = (userId) =>
    useSelector((state) => state.liveMode?.raisedHands?.includes(userId) ?? false)

export const useIsOngoingCheckpoint = (userId) =>
    useSelector((state) => state.liveMode?.ongoingCheckpointIds?.includes(userId) ?? false)

export const useIsParticipantOnline = (userId, currentUserId) =>
    useSelector((state) => {
        if (userId === currentUserId) return true
        return state.liveMode?.liveParticipants?.includes(userId) ?? false
    })

export const useParticipantActiveTime = (userId) =>
    useSelector((state) => state.liveMode?.activeTime?.find((x) => x?.id === userId)?.time ?? 0)

export const useTimeSinceLastCall = (userId) =>
    useSelector((state) => state.liveMode?.timesSinceLastCall?.[userId] ?? 0)

export const useAgentNotifCount = (userId, liveSessionId) =>
    useSelector((state) => {
        const match = state.liveMode?.agentNotifCounts?.find(
            (n) => n?.studentId === userId && n?.liveSessionId === liveSessionId
        )
        return match?.count ?? 0
    })

export const useCompletedConversation = (userId) =>
    useSelector((state) => {
        const convs = state.liveMode?.completedConversations
        if (!Array.isArray(convs)) return null
        return convs.find((c) => c?.studentId === userId) ?? null
    }, shallowEqual)


export const useIsOutOfApp = (userId) =>
    useSelector((state) => state.liveMode?.outOfAppIds?.includes(userId) ?? false)

export const useIsActivityCameraOn = (userId) =>
    useSelector((state) => state.liveMode?.activityCameraOnIds?.includes(userId) ?? false)

// The activity the student has in front of them, on any of their devices in
// the room (`seenStudentActivity`, per device: hidden under the visio on a
// phone it is none). Before the room is read, the opened one, as before.
// `currentStudentActivity` keeps the opened activity for what follows it
// (the pinned student's focus view, the checkpoint rules).
export const useSeenStudentActivity = (userId) =>
    useSelector((state) => {
        const devices = Object.entries(
            state.liveMode?.seenStudentActivity ?? {}
        ).filter(([identity]) => parseInt(identity) === userId)
        if (devices.length === 0) {
            return state.liveMode?.currentStudentActivity?.[userId] ?? null
        }
        return devices.find(([, activityId]) => activityId != null)?.[1] ?? null
    })

export const useSoundModeParticipantCount = () =>
    useSelector((state) => state.liveMode?.soundModeIds?.length ?? 0)
